import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { trackAddToCart } from "../lib/track";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

function getSessionId() {
  let sid = localStorage.getItem("fl_session_id");
  if (!sid) {
    sid = "guest_" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem("fl_session_id", sid);
  }
  return sid;
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const params = user ? {} : { session_id: getSessionId() };
      const response = await axios.get(`${API}/cart`, { withCredentials: true, params });
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, size = "standard") => {
    try {
      const params = user ? {} : { session_id: getSessionId() };
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity, size }, { withCredentials: true, params });
      await fetchCart();
      axios.get(`${API}/products/${productId}`).then(r => trackAddToCart({ ...r.data, id: productId }, quantity)).catch(() => {});
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      return false;
    }
  };

  const updateCartItem = async (productId, quantity, size = "standard") => {
    try {
      const params = user ? {} : { session_id: getSessionId() };
      await axios.put(`${API}/cart/update`, { product_id: productId, quantity, size }, { withCredentials: true, params });
      await fetchCart();
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  };

  const removeFromCart = async (productId, size = "standard") => {
    try {
      const params = user ? { size } : { session_id: getSessionId(), size };
      await axios.delete(`${API}/cart/remove/${productId}`, { withCredentials: true, params });
      await fetchCart();
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const clearCart = async () => {
    try {
      const params = user ? {} : { session_id: getSessionId() };
      await axios.delete(`${API}/cart/clear`, { withCredentials: true, params });
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart, cartCount, loading,
      addToCart, updateCartItem, removeFromCart, clearCart, fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
